<template>
  <ViewContainer :title="$t('routines.routine_other')">
    <template v-slot:header-right>
      <CreateButton
        v-if="canCreateRoutines"
        small
        data-testid="routineList-button-createRoutine"
        @click="createRoutine('createButton')"
      />
      <OrganizationSelector
        v-if="account.isAdmin"
        class="ms-4"
        :selectedOrganizationId="selectedOrganizationId"
        @change="handleOrganizationChange"
      />
    </template>
    <template slot="append">
      <div class="d-flex align-center ms-2 my-n1">
        <SearchInput
          ref="searchInput"
          activeClickSearch
          style="min-width: 300px"
          autofocus
          :value="searchTerms"
          :disabled="loading"
          @change="handleSearch"
        />
      </div>
    </template>

    <v-card class="pa-6">
      <Filters
        class="me-1 mb-2"
        :searchTerms="searchTerms"
        :viewType="viewType"
        :totalItems="totalItems"
        :loading="loading"
        @change="handleFiltersChange"
        @sortChange="handleSortChange"
        @viewSelected="handleViewChange"
      />

      <LoaderSpinner
        v-if="loading && viewType === VIEW_OPTIONS.cards"
        style="height: 60vh"
        class="d-flex justify-center align-center"
      />
      <EmptyState
        v-if="!definitions.length && !loading"
        :icon="emptyStateIcon"
        style="height: 60vh"
        :iconSize="32"
        :title="emptyStateTitle"
        :message="emptyStateMessage"
        :buttonText="emptyStateButtonText"
        :buttonTourId="'create-routine-button'"
        :buttonTestId="'routineList-button-createRoutine'"
        @click="createRoutine('routineListEmptyState')"
      />

      <RoutineDefinitionsCardsView
        v-if="isCardView"
        :loading="loading"
        :definitions="definitions"
        :organizationId="organizationId"
        :totalPages="totalPages"
        :totalRecords="totalRecordsInData"
        :currentPage="currentPage"
        @handlePageChange="handlePageChange"
        @getRoutines="getRoutines"
      />

      <RoutineDefinitionsTableView
        v-if="isTableView"
        :loading="loading"
        :definitions="definitions"
        :totalRecords="totalRecordsInData"
        :currentPage="currentPage"
        :selectedOrganizationId="organizationId"
        @handleOptionsChange="handleOptionsChange"
        @getRoutines="getRoutines"
        @startRoutine="startRoutine"
      />
    </v-card>
    <CreateRoutineModal
      v-if="openCreateDialog"
      :show="openCreateDialog"
      @close="openCreateDialog = false"
    />
    <RoutineStartDialog
      :open="showStartRoutineDialog"
      :routine="definitionToStart"
      @cancel="showStartRoutineDialog = false"
      @started="
        navigateTo({
          name: 'routineExecutionDetail',
          params: { routineId: $event },
        })
      "
    />
  </ViewContainer>
</template>

<script>
import { mapGetters } from 'vuex'
import _ from 'lodash'
import mixin from '@/mixins/mixin'
import api from '@/api'
import routineDefinitionPermissions from '@/mixins/routineDefinitionPermissions.vue'
import trackingMixin from '@/mixins/tracking/trackerMixin.vue'

import LoaderSpinner from '@/components/utils/LoaderSpinner.vue'
import CreateButton from '@/components/UI/CreateButton'
import ViewContainer from '@/components/Core/Layout/ViewContainer'
import EmptyState from '@/components/EmptyState.vue'
import OrganizationSelector from '@/components/OrganizationSelector.vue'
import CreateRoutineModal from './CreateRoutineModal/CreateRoutineModal.vue'
import Filters from './Filters.vue'
import SearchInput from '@/components/Core/SearchInput.vue'
import RoutineDefinitionsCardsView from './RoutineDefinitionsCardsView.vue'
import RoutineDefinitionsTableView from './RoutineDefinitionsTableView.vue'
import RoutineStartDialog from '@/components/RoutineStartModal.vue'

const VIEW_OPTIONS = {
  cards: 'cards',
  table: 'table',
}

export default {
  name: 'RoutineDefinitionsView',

  mixins: [mixin, trackingMixin, routineDefinitionPermissions],

  components: {
    CreateButton,
    CreateRoutineModal,
    EmptyState,
    Filters,
    LoaderSpinner,
    OrganizationSelector,
    SearchInput,
    ViewContainer,
    RoutineDefinitionsCardsView,
    RoutineDefinitionsTableView,
    RoutineStartDialog,
  },

  data() {
    return {
      VIEW_OPTIONS,
      viewType: VIEW_OPTIONS.cards,
      currentPage: parseInt(this.$route?.query?.currentPage ?? 1),
      defaultFiltersValidOptions: {},
      definitions: [],
      hasTemplates: false,
      loading: true,
      openCreateDialog: false,
      searchTerms: this.$route?.query?.searchTerms ?? null,
      selectedFilters: {},
      selectedOrganizationId: null, // Only available for rever admins
      sort: {
        by: 'createdAt',
        order: 'desc',
      },
      totalPages: 0,
      totalRecordsInData: 0,
      showStartRoutineDialog: false,
      definitionToStart: null,
      ITEMS_PER_PAGE: 9,
      persistenceKey: { type: String, default: 'routine_definitions_view' },
    }
  },

  created() {
    this.defaultFiltersValidOptions = {
      assignees: [this.accountId],
      isExecutableByMe: true,
    }
  },

  mounted() {
    this.retrieveInitialViewState()
    if (!this.isRegularUser) this.checkHasTemplates()
  },

  computed: {
    ...mapGetters({
      accountOrganizationId: 'organizationId',
      siteId: 'siteId',
      accountId: 'accountId',
      account: 'account',
    }),

    isCardView() {
      return this.viewType === VIEW_OPTIONS.cards
    },

    isTableView() {
      return this.viewType === VIEW_OPTIONS.table
    },

    totalItems() {
      return this.definitions.length
    },

    organizationId() {
      // Rever admins can manually set organizationId for testing purposes
      if (!this.account.isAdmin) return this.accountOrganizationId
      return this.selectedOrganizationId || this.accountOrganizationId
    },

    onlyDefaultFilterIsSelected() {
      const hasOnlyDirectlyAssignedToMe =
        this.selectedFilters?.assignees &&
        _.isArray(this.selectedFilters.assignees) &&
        this.selectedFilters.assignees.length === 1 &&
        this.selectedFilters.assignees[0] === this.accountId

      const hasNoOtherFilters = Object.keys(this.selectedFilters).length === 1

      return hasOnlyDirectlyAssignedToMe && hasNoOtherFilters
    },

    defaultFilterIsSelected() {
      const selectedValue = this.selectedFilters.assignees
      const optionValue = this.defaultFiltersValidOptions.assignees
      const isDirectlyAssignedToMe = _.isEqual(selectedValue, optionValue)

      return isDirectlyAssignedToMe
    },

    emptyStateIcon() {
      return this.onlyDefaultFilterIsSelected
        ? 'mdi-clipboard-text-outline'
        : 'mdi-ghost-outline'
    },

    emptyStateTitle() {
      if (this.onlyDefaultFilterIsSelected) {
        return this.isRegularUser
          ? this.$t('routines.empty_state_title_regular_user')
          : this.$t('explore.empty_state_title')
      } else {
        return this.$t('explore.empty_state_title')
      }
    },

    emptyStateMessage() {
      if (this.onlyDefaultFilterIsSelected) {
        return this.isRegularUser
          ? this.$t('routines.empty_state_description_regular_user')
          : this.$t('routines.empty_state_description')
      } else {
        return this.$t('routines.empty_state_filters')
      }
    },

    emptyStateButtonText() {
      return this.isRegularUser || !this.onlyDefaultFilterIsSelected
        ? null
        : this.$t('routines.create_new_routine')
    },

    isOrganizationAdminOrReverAdmin() {
      return this.account.isAdmin || this.account.isOrganizationAdmin
    },

    isRoutineManagerOrSiteAdmin() {
      return this.account.isSiteAdmin || this.account.isRoutineManager
    },

    isRegularUser() {
      return (
        !this.isOrganizationAdminOrReverAdmin &&
        !this.isRoutineManagerOrSiteAdmin
      )
    },

    canCreateRoutines() {
      return this.getRoutineDefinitionPermissions({}).create
    },

    defaultSearchHighlights() {
      return [this.searchTerms ?? '', ...(this.searchTerms?.split?.(' ') ?? [])]
    },
  },

  methods: {
    handleOrganizationChange(organizationId) {
      this.selectedOrganizationId = organizationId
      this.getRoutines()
    },

    async handleSearch(terms) {
      this.searchTerms = terms
      this.currentPage = 1
      this.sort = terms
        ? { by: '_score', order: 'desc' }
        : {
            by: 'createdAt',
            order: 'desc',
          }
      this.updateRoute()
      this.trackRoutineList({
        action: 'filter',
        term: terms,
        filters: this.selectedFilters,
      })
      await this.getRoutines()
      await this.$nextTick()

      this.$refs.searchInput?.$children?.[0]?.focus?.()
    },

    handleFiltersChange(selectedFilters) {
      this.currentPage = 1

      if (_.isEqual(selectedFilters, this.selectedFilters)) return

      this.selectedFilters = { ...selectedFilters }
      this.updateRoute()
      this.getRoutines()

      this.trackRoutineList({
        action: 'filter',
        term: this.searchTerms,
        filters: { ...selectedFilters },
      })
    },

    updateRoute() {
      const newQuery = {
        ...this.selectedFilters,
        searchTerms: this.searchTerms,
        currentPage: this.currentPage,
      }

      const newQueryParsed = Object.entries(newQuery).reduce(
        (previousValue, [currentKey, currentValue]) => {
          if (currentValue?.from && currentValue?.to) {
            return (previousValue = {
              ...previousValue,
              [currentKey]: [currentValue.from, currentValue.to],
            })
          }

          return (previousValue = {
            ...previousValue,
            [currentKey]: currentValue,
          })
        },
        {}
      )

      this.replaceRoute({
        name: 'routineDefinitions',
        query: newQueryParsed,
      })
    },

    async checkHasTemplates() {
      try {
        const checkTemplates = await api.routine.getStreamRoutineDefinition({
          organizationId: this.organizationId,
          filters: { isGlobalTemplate: true },
          limit: 1,
        })
        const templates = checkTemplates?.data?.results ?? []
        this.hasTemplates = !_.isEmpty(templates)
      } catch (err) {
        this.handleRequestError(err)
      }
    },

    async getRoutines() {
      try {
        this.loading = true

        const filters = {
          ...this.selectedFilters,
          isActive: _.isBoolean(this.selectedFilters.isActive)
            ? this.selectedFilters.isActive
            : true,
        }

        if (this.defaultFilterIsSelected) {
          filters.isGloballyAssigned = false
        }

        const params = {
          skip: (this.currentPage - 1) * this.ITEMS_PER_PAGE,
          limit: this.ITEMS_PER_PAGE,
          filters,
          terms: this.searchTerms,
          sort: this.sort,
        }

        params.filters.types = params.filters.type
        delete params.filters.type

        if (params.filters.executableInSite) {
          params.filters.isGloballyAssigned = false
        }

        const response = await api.routine.getStreamRoutineDefinition({
          ...params,
          from: params.skip,
          organizationId: this.organizationId,
        })

        this.definitions = response.data.results.map(item => ({
          definition: item.result,
          highlights: item.highlights?.name ?? this.defaultSearchHighlights,
        }))
        this.totalRecordsInData = response?.data?.totalRecordsInData || 0

        this.totalPages =
          Math.ceil(response?.data?.totalRecordsInData / this.ITEMS_PER_PAGE) ||
          0
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.loading = false
      }
    },

    handlePageChange(page) {
      if (this.currentPage === page) return

      this.trackRoutineList({ action: 'pageChange', page: page })

      this.currentPage = page
      this.updateRoute()
      this.getRoutines()
    },

    createRoutine(source) {
      if (!this.hasTemplates) {
        this.trackRoutineViewNavigate({
          source: 'routineDefinitionsView',
          action: 'goToCreateScreen',
          fromTemplate: false,
        })
        return this.navigateTo({ name: 'createRoutineDefinition' })
      }

      this.trackRoutineViewNavigate({
        action: 'openCreationDialog',
        source: source,
      })
      this.openCreateDialog = true
    },

    trackRoutineViewNavigate() {
      this.trackRoutineList({
        action: 'clickPermissionHelperText',
        source: 'routineDefinitions',
      })
    },

    handlePermissionClick() {
      this.trackRoutineListPermissionHelper()
    },

    handleSortChange(sort) {
      this.sort = sort
      this.currentPage = 1
      this.getRoutines()
    },

    startRoutine(definition, source) {
      this.definitionToStart = definition

      this.trackRoutineViewNavigate({
        action: 'goToStart',
        source: source,
        id: definition.id,
      })
      this.showStartRoutineDialog = true
    },

    handleOptionsChange(options) {
      this.currentPage = options.page
      this.ITEMS_PER_PAGE = options.itemsPerPage

      this.updateRoute()
      this.getRoutines()
    },

    handleViewChange(option) {
      if (this.viewType === option) return

      if (option !== this.viewType) {
        this.definitions = []
        this.searchTerms = null
        this.currentPage = 1
        this.updateRoute()
      }

      if (option === VIEW_OPTIONS.cards) {
        this.ITEMS_PER_PAGE = 9
        this.getRoutines()
      }

      this.viewType = option
      this.persistViewState()

      this.trackRoutineList({
        action: 'changeView',
        newView: option,
      })
    },

    persistViewState() {
      localStorage.setItem(this.persistenceKey + '__viewType', this.viewType)
    },

    retrieveInitialViewState() {
      const view = localStorage.getItem(this.persistenceKey + '__viewType')

      if (!view) return

      this.viewType = view
    },
  },
}
</script>
